import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotices } from '../context/NoticeContext';
import { getNoticeDetail } from '../services/api';
import NoticeForm from './NoticeForm';
import type { Notice } from '../types/notice';

interface NoticeDetailProps {
  id: number;
  onBack: () => void;
}

const NoticeDetail: React.FC<NoticeDetailProps> = ({ id, onBack }) => {
  const { removeNotice, loading, error } = useNotices();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [action, setAction] = useState<'delete' | 'edit' | null>(null);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const API_BASE_URL = 'https://visit.dongan1718.com/api'; // 백엔드 API 엔드포인트
  //const API_BASE_URL = 'http://localhost:3001/api'; // 백엔드 API 엔드포인트
  useEffect(() => {
    const fetchNotice = async () => {
      try {
        const response = await getNoticeDetail(id);
        if (response.success && response.data) {
          setNotice(response.data);
        } else {
          alert('공지사항을 불러오는데 실패했습니다.');
        }
      } catch (err) {
        alert('공지사항을 불러오는데 실패했습니다.');
      }
    };

    fetchNotice();
  }, [id]);

  const handlePasswordSubmit = async () => {
    if (!password) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    if (action === 'delete') {
      try {
        const success = await removeNotice(id, password);
        if (success) {
          onBack();
        } else {
          alert('비밀번호가 일치하지 않습니다.');
        }
      } catch (err) {
        alert('삭제 중 오류가 발생했습니다.');
      }
    } else if (action === 'edit') {
      try {
        // 서버에서 비밀번호 검증
        const response = await fetch(`${API_BASE_URL}/notices/${id}/verify-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password }),
        });
        const result = await response.json();

        if (result.success) {
          setShowEditForm(true);
          setShowPasswordModal(false);
        } else {
          alert('비밀번호가 일치하지 않습니다.');
        }
      } catch (err) {
        alert('비밀번호 확인 중 오류가 발생했습니다.');
      }
    }
  };

  if (loading) {
    return <LoadingMessage>로딩 중...</LoadingMessage>;
  }

  if (!notice) {
    return <ErrorMessage>공지사항을 찾을 수 없습니다.</ErrorMessage>;
  }

  if (showEditForm) {
    return (
      <NoticeForm
        onBack={onBack}
        editId={id}
        initialTitle={notice.title}
        initialContent={notice.content}
      />
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <BackIcon>&larr;</BackIcon>
          <span>목록</span>
        </BackButton>
        <Title>공지사항</Title>
      </Header>

      <NoticeContainer>
        <NoticeHeader>
          <h2>{notice.title}</h2>
          <NoticeInfo>
            <span>작성일: {new Date(notice.created_at).toLocaleDateString()}</span>
            <span>조회수: {notice.views}</span>
          </NoticeInfo>
        </NoticeHeader>
        <NoticeBody>
          {notice.content.split('\n').map((line: string, index: number) => (
            <p key={index}>{line}</p>
          ))}
        </NoticeBody>
        <ButtonGroup>
          <EditButton
            onClick={() => {
              setAction('edit');
              setShowPasswordModal(true);
            }}
          >
            수정
          </EditButton>
          <DeleteButton
            onClick={() => {
              setAction('delete');
              setShowPasswordModal(true);
            }}
          >
            삭제
          </DeleteButton>
        </ButtonGroup>
      </NoticeContainer>

      {showPasswordModal && (
        <PasswordModal>
          <ModalContent>
            <h3>{action === 'delete' ? '삭제하기' : '수정하기'}</h3>
            <p>비밀번호를 입력해주세요.</p>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
            />
            <ModalButtonGroup>
              <CancelButton
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                }}
              >
                취소
              </CancelButton>
              <ConfirmButton onClick={handlePasswordSubmit}>확인</ConfirmButton>
            </ModalButtonGroup>
          </ModalContent>
        </PasswordModal>
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 5px;
  position: absolute;
  left: 0;
`;

const BackIcon = styled.span`
  font-size: 20px;
  margin-right: 5px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
  text-align: center;
`;

const NoticeContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background-color: #fff;
`;

const NoticeHeader = styled.div`
  border-bottom: 1px solid #ddd;
  padding-bottom: 15px;
  margin-bottom: 20px;

  h2 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 10px;
  }
`;

const NoticeInfo = styled.div`
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 0.9rem;
`;

const NoticeBody = styled.div`
  line-height: 1.6;
  white-space: pre-wrap;

  p {
    margin-bottom: 10px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
`;

const EditButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  border: none;

  &:hover {
    background-color: #357abd;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #e74c3c;
  color: white;
  border: none;

  &:hover {
    background-color: #c0392b;
  }
`;

const PasswordModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  text-align: center;

  h3 {
    margin-bottom: 15px;
  }

  p {
    margin-bottom: 15px;
    color: #666;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 15px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const CancelButton = styled(Button)`
  background-color: #f1f1f1;
  color: #333;
  border: 1px solid #ddd;

  &:hover {
    background-color: #e1e1e1;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  border: none;

  &:hover {
    background-color: #357abd;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  padding: 20px;
`;

export default NoticeDetail;
